import React from 'react';
import moment from 'moment';
import { renderBitacoraCommentWithMentions } from './renderMarkdown';
import '../../repositorio/HistoryList.styles.scss';

/**
 * Traduce event de editionHistory (estilo obligaciones/entries) a verbo para el historial.
 */
const eventToAction = (event) => {
  const t = {
    CREATE: 'CREATED',
    CREATED: 'CREATED',
    EDIT: 'UPDATED',
    UPDATE: 'UPDATED',
    UPDATED: 'UPDATED',
    SEND: 'NOTIFY',
    NOTIFY: 'NOTIFY',
    DELIVER: 'NOTIFY',
    COMMENT: 'COMMENT',
    DELETE: 'DELETE',
  };
  return t[String(event).toUpperCase()] || 'UPDATED';
};

/**
 * Construye el array de historial. Si la bitácora tiene editionHistory (guardado como en obligaciones),
 * se usa eso; si no, se deriva de createdAt, updatedAt, deliveredAt y comments.
 */
const buildHistoryFromBitacora = (bitacora) => {
  const editionHistory = bitacora?.editionHistory;
  if (editionHistory && editionHistory.length > 0) {
    const entries = editionHistory.map((e, idx) => {
      const d = e.date ? moment(e.date) : moment();
      const details = typeof e.field === 'string' ? e.field : (e.field?.oldValue != null ? `${e.field.key}: ${e.field.oldValue} → ${e.field.value}` : e.field?.key ? `${e.field.key}: ${e.field.value}` : null) || (e.fields?.length ? e.fields.map((f) => (f.oldValue != null ? `${f.key}: ${f.oldValue} → ${f.value}` : `${f.key}: ${f.value}`)).join('; ') : null);
      const event = e.event;
      return {
        id: `bitacora-${bitacora?.id}-edition-${idx}-${e.date || idx}`,
        userEmail: e.userEmail || e.userId || 'Usuario',
        action: event ? eventToAction(event) : 'UPDATED',
        date: d.format('DD/MM/YYYY'),
        time: d.format('HH:mm'),
        details: details || (event === 'SEND' || event === 'NOTIFY' ? 'Documento enviado al cliente' : 'Bitácora actualizada'),
      };
    });
    return entries.sort((a, b) => {
      const dateA = moment(a.date, 'DD/MM/YYYY').toDate();
      const dateB = moment(b.date, 'DD/MM/YYYY').toDate();
      if (dateA.getTime() !== dateB.getTime()) return dateB - dateA;
      return (b.time || '').localeCompare(a.time || '');
    });
  }

  const entries = [];
  if (bitacora?.createdAt) {
    const d = moment(bitacora.createdAt);
    entries.push({
      id: `bitacora-${bitacora.id}-created`,
      userEmail: bitacora.createdByEmail || 'Sistema',
      action: 'CREATED',
      date: d.format('DD/MM/YYYY'),
      time: d.format('HH:mm'),
      details: 'Bitácora creada',
    });
  }
  if (bitacora?.updatedAt && bitacora?.updatedAt !== bitacora?.createdAt) {
    const d = moment(bitacora.updatedAt);
    entries.push({
      id: `bitacora-${bitacora.id}-updated`,
      userEmail: bitacora.updatedByEmail || bitacora.updatedBy || 'Sistema',
      action: 'UPDATED',
      date: d.format('DD/MM/YYYY'),
      time: d.format('HH:mm'),
      details: 'Bitácora actualizada',
    });
  }
  if (bitacora?.deliveredAt && (bitacora?.deliveredByEmail || bitacora?.deliveredBy)) {
    const d = moment(bitacora.deliveredAt);
    entries.push({
      id: `bitacora-${bitacora.id}-delivery`,
      userEmail: bitacora.deliveredByEmail || bitacora.deliveredBy,
      action: 'NOTIFY',
      date: d.format('DD/MM/YYYY'),
      time: d.format('HH:mm'),
      details: 'Documento enviado al cliente',
    });
  }
  const comments = bitacora?.comments || [];
  comments.forEach((c, idx) => {
    const date = c.date ? moment(c.date) : moment();
    const user = c.user?.email || c.user?.name || 'Usuario';
    entries.push({
      id: `bitacora-${bitacora?.id}-comment-${idx}-${c.id || idx}`,
      userEmail: user,
      action: 'COMMENT',
      date: date.format('DD/MM/YYYY'),
      time: date.format('HH:mm'),
      details: c.comment || null,
    });
  });
  return entries.sort((a, b) => {
    const dateA = moment(a.date, 'DD/MM/YYYY').toDate();
    const dateB = moment(b.date, 'DD/MM/YYYY').toDate();
    if (dateA.getTime() !== dateB.getTime()) return dateB - dateA;
    return (b.time || '').localeCompare(a.time || '');
  });
};

const translateAction = (action) => {
  const t = { CREATED: 'creó', UPDATED: 'actualizó', NOTIFY: 'notificó', COMMENT: 'comentó', DELETE: 'eliminó' };
  return t[action] || action?.toLowerCase() || '';
};

const getActionColor = () => '#176f80';

const getActionIcon = (action) => {
  if (action === 'NOTIFY') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 2L11 13" />
        <path d="M22 2L15 22L11 13L2 9L22 2z" />
      </svg>
    );
  }
  if (action === 'UPDATED') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-3-6.7" />
        <polyline points="21 3 21 9 15 9" />
      </svg>
    );
  }
  if (action === 'CREATED') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </svg>
    );
  }
  if (action === 'DELETE') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
};

const BitacoraHistoryList = ({ bitacora }) => {
  const history = buildHistoryFromBitacora(bitacora);
  const displayTitle = bitacora?.title || (bitacora?.flowDate ? moment(bitacora.flowDate).locale('es').format('D [de] MMMM [de] YYYY') : 'Bitácora');
  const isDelivered = bitacora?.status === 'delivered';

  if (history.length === 0) {
    return (
      <div className="history-list-empty">
        <p>No hay historial de cambios para esta bitácora.</p>
      </div>
    );
  }

  return (
    <div className="history-list-container">
      <div className="history-list-period-header">
        <div className="history-list-period-header-inner">
          <div className="history-list-period-info-row">
            <div className="history-list-period-left-group">
              <div className="history-list-period-title">{displayTitle}</div>
              <span className="history-list-period-separator">|</span>
              <span className="history-list-period-count">
                {history.length} {history.length === 1 ? 'evento' : 'eventos'}
              </span>
            </div>
            <div className="history-list-period-right-group">
              <span className={`history-list-period-status history-list-period-status--${isDelivered ? 'DELIVERED' : 'pending'}`}>
                {isDelivered ? 'Entregado' : 'Borrador'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="history-list-items">
        <div className={`history-list-items-inner ${history.length >= 2 ? 'history-list-items-inner--multiple' : ''}`}>
        {history.map((item, index) => (
          <div key={item.id || index} className="history-list-row">
            <div className="history-list-item">
              <div className="history-list-avatar" style={{ backgroundColor: getActionColor() }}>
                {getActionIcon(item.action)}
              </div>
              <div className="history-list-body">
                <div className="history-list-header">
                  <span className="history-list-user">{item.userEmail}</span>
                  <span className="history-list-action-verb">{translateAction(item.action)}</span>
                  <span className="history-list-timeline-label">un item</span>
                  <span className="history-list-time">{item.date} a las {item.time || '00:00'}</span>
                </div>
                {item.details && (
                  item.action === 'COMMENT' ? (
                    <div className="history-list-comment-box">
                      <div
                        className="history-list-text bitacora-comments-text bitacora-comments-text--markdown bitacora-editor-content"
                        dangerouslySetInnerHTML={{ __html: renderBitacoraCommentWithMentions(item.details) }}
                      />
                    </div>
                  ) : (
                    <div className="history-list-text">{item.details}</div>
                  )
                )}
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
};

export default BitacoraHistoryList;
